import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, Plus, X, PauseCircle, PlayCircle, Copy, QrCode, Pencil, Trash2, KeyRound, Calendar, RotateCw, MoreVertical, Bell } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const FREE_UNTIL_LAUNCH = "FREE_UNTIL_LAUNCH";
const DEFAULT_FREE_UNTIL_LAUNCH_MESSAGE = (
  "هذا المطعم يستخدم خِفّه مجانًا حتى الإطلاق الرسمي. "
  + "سيتم إشعاركم مسبقًا قبل بدء الاشتراك المدفوع."
);
const STATUS_LABEL = {
  TRIAL: "تجربة",
  ACTIVE: "نشط",
  EXPIRED: "منتهي",
  SUSPENDED: "موقوف",
  ARCHIVED: "مؤرشف",
  FREE_UNTIL_LAUNCH: "مجانًا حتى الإطلاق",
};
const STATUS_CLS = {
  TRIAL: "bg-amber-100 text-amber-800",
  ACTIVE: "bg-green-100 text-green-800",
  EXPIRED: "bg-red-100 text-red-800",
  SUSPENDED: "bg-red-100 text-red-800",
  ARCHIVED: "bg-gray-200 text-gray-600",
  FREE_UNTIL_LAUNCH: "bg-teal-100 text-teal-800",
};

export default function Admin() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [rs, setRs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);   // restaurant being edited
  const [deleting, setDeleting] = useState(null); // restaurant being archived
  const [resetPwFor, setResetPwFor] = useState(null); // restaurant whose owner password is being reset
  const [extendFor, setExtendFor] = useState(null);   // restaurant whose account is being extended
  const [qrFor, setQrFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);

  const load = () => api.get("/admin/restaurants")
    .then(r => setRs(r.data))
    .catch(e => toast.error(formatErr(e)))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const changeStatus = async (r, status) => {
    try { await api.patch(`/admin/restaurants/${r.id}`, { status }); load(); toast.success("تم التحديث"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-white border-b border-orange-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl brand-grad text-white grid place-items-center font-extrabold">خ</div>
            <div>
              <div className="text-xs text-gray-500">لوحة المشرف</div>
              <div className="font-extrabold">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <button onClick={() => setShowMenu(true)} className="pill bg-white border border-orange-100 text-gray-600 px-2.5 py-2" data-testid="admin-menu-btn" aria-label="المزيد">
            <MoreVertical size={16}/>
          </button>
          <button onClick={() => { logout(); nav("/login"); }} className="pill text-red-600 border border-red-100 bg-red-50 px-3 py-2 text-sm font-bold inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={14}/> خروج</button>
          </div>
        </div>
      </header>

      {showMenu && (
        <div className="fixed inset-0 z-50" data-testid="admin-side-panel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMenu(false)} />
          <div className="absolute top-0 end-0 h-full w-72 bg-white shadow-xl border-s border-orange-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-extrabold">القائمة</div>
              <button onClick={() => setShowMenu(false)} className="pill bg-gray-50 border border-gray-200 px-2.5 py-2" data-testid="admin-side-panel-close"><X size={16}/></button>
            </div>
            <Link to="/admin/notifications" onClick={() => setShowMenu(false)}
              className="w-full flex items-center gap-2 rounded-2xl border border-orange-100 bg-[#FAF7F2] px-4 py-3 font-bold text-sm"
              data-testid="admin-menu-notifications">
              <Bell size={16} className="text-orange-600"/> الإشعارات
            </Link>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-5 py-5">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-extrabold">المطاعم</h1>
          <button onClick={() => setShowAdd(true)} className="pill brand-grad text-white px-4 py-2.5 font-bold text-sm inline-flex items-center gap-1" data-testid="add-restaurant-btn">
            <Plus size={16}/> إضافة مطعم
          </button>
        </div>

        {loading && <div className="text-gray-500">جاري التحميل…</div>}
        {!loading && rs.length === 0 && (
          <div className="text-center py-16 bg-white rounded-3xl border border-orange-100">
            <div className="text-5xl mb-3">🏪</div>
            <div className="font-extrabold text-lg">لا توجد مطاعم حقيقية بعد</div>
            <div className="text-sm text-gray-600 mt-1 mb-4">ابدأ بإضافة أول مطعم للمنصة</div>
            <button onClick={() => setShowAdd(true)} className="pill brand-grad text-white px-5 py-3 font-bold inline-flex items-center gap-1" data-testid="add-first-btn">
              <Plus size={16}/> إضافة أول مطعم
            </button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {rs.map(r => {
            const url = `${window.location.origin}/r/${r.slug}`;
            return (
            <div key={r.id} className="bg-white rounded-2xl border border-orange-100 card-shadow p-4" data-testid={`admin-r-${r.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-extrabold text-lg">{r.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5 num-badge flex items-center gap-1">
                    /r/{r.slug}
                    <button onClick={async () => {
                      const next = window.prompt("رابط جديد (حروف لاتينية وأرقام و - فقط):", r.slug);
                      if (!next || next === r.slug) return;
                      try { await api.patch(`/admin/restaurants/${r.id}`, { slug: next }); load(); toast.success("تم تغيير الرابط"); }
                      catch (e) { toast.error(formatErr(e)); }
                    }} className="text-orange-600" data-testid={`rename-${r.slug}`} title="تعديل الرابط"><Pencil size={12}/></button>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`pill text-xs font-bold px-3 py-1 ${STATUS_CLS[r.account?.account_status || r.status] || "bg-gray-100 text-gray-600"}`}>{STATUS_LABEL[r.account?.account_status || r.status] || r.status}</span>
                  {r.account?.days_remaining !== null && r.account?.days_remaining !== undefined && (
                    <span className={`num-badge text-[11px] ${r.account.is_expired ? "text-red-700" : r.account.days_remaining <= 7 ? "text-amber-700" : "text-gray-600"}`} data-testid={`days-${r.slug}`}>
                      {r.account.is_expired ? "منتهية الصلاحية" :
                        r.account.days_remaining === 0 ? "ينتهي اليوم" :
                        r.account.days_remaining === 1 ? "يوم واحد" :
                        r.account.days_remaining === 2 ? "يومان" :
                        `${r.account.days_remaining} يوم متبقي`}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-600 space-y-1">
                <div>المالك: <span className="font-medium text-gray-900">{r.owner_name}</span></div>
                <div>البريد: <span className="num-badge text-gray-800">{r.owner_email}</span></div>
                <div>الطلبات: <span className="num-badge font-bold text-gray-900">{r.orders_count}</span></div>
                <div className="text-xs text-gray-500">أُنشئ: {new Date(r.created_at).toLocaleDateString("ar-SA")}</div>
                {r.account?.account_expires_at && (
                  <div className="text-xs text-gray-500" data-testid={`expires-${r.slug}`}>
                    ينتهي: {new Date(r.account.account_expires_at).toLocaleDateString("ar-SA")}
                  </div>
                )}
              </div>
              <div className="mt-3 rounded-xl bg-gray-50 px-2 py-1.5 text-xs num-badge break-all">{url}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => setEditing(r)} className="pill bg-white border border-orange-200 text-orange-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`edit-${r.slug}`}><Pencil size={12}/> تعديل</button>
                <button onClick={() => setResetPwFor(r)} className="pill bg-white border border-amber-200 text-amber-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`reset-pw-${r.slug}`}><KeyRound size={12}/> كلمة المرور</button>
                {r.status !== FREE_UNTIL_LAUNCH && (
                  <button
                    onClick={() => setExtendFor(r)}
                    className={
                      "pill inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 "
                      + "px-3 py-1.5 text-xs font-bold text-emerald-800"
                    }
                    data-testid={`extend-${r.slug}`}
                  >
                    <Calendar size={12}/>
                    {r.account?.is_expired ? "تفعيل / تمديد" : "تمديد"}
                  </button>
                )}
                {r.status === FREE_UNTIL_LAUNCH && (
                  <button
                    onClick={() => changeStatus(r, "TRIAL")}
                    className={
                      "pill inline-flex items-center gap-1 border border-amber-200 bg-amber-50 "
                      + "px-3 py-1.5 text-xs font-bold text-amber-800"
                    }
                    data-testid={`remove-free-${r.slug}`}
                  >
                    <Calendar size={12}/>
                    إنهاء المجانية
                  </button>
                )}
                <button onClick={() => { navigator.clipboard.writeText(url); toast.success("تم النسخ"); }} className="pill bg-white border border-orange-200 text-orange-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`copy-${r.slug}`}><Copy size={12}/> نسخ</button>
                <a href={url} target="_blank" rel="noreferrer" className="pill bg-white border border-orange-200 text-orange-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`open-${r.slug}`}><ExternalLink size={12}/> فتح</a>
                <button onClick={() => setQrFor({ url, name: r.name, slug: r.slug })} className="pill bg-white border border-orange-200 text-orange-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`qr-${r.slug}`}><QrCode size={12}/> QR</button>
                <a href={`/admin/qr-poster?slug=${r.slug}`} className="pill brand-grad text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`poster-${r.slug}`}>🖨️ ملصق</a>
                {r.status !== "SUSPENDED" ? (
                  <button onClick={() => changeStatus(r, "SUSPENDED")} className="pill bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`suspend-${r.slug}`}><PauseCircle size={12}/> إيقاف</button>
                ) : (
                  <button onClick={() => changeStatus(r, "ACTIVE")} className="pill bg-green-50 border border-green-100 text-green-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`activate-${r.slug}`}><PlayCircle size={12}/> تفعيل</button>
                )}
                {r.status === "TRIAL" && (
                  <button onClick={() => changeStatus(r, "ACTIVE")} className="pill bg-orange-50 border border-orange-100 text-orange-700 px-3 py-1.5 text-xs font-bold" data-testid={`activate-trial-${r.slug}`}>تفعيل الحساب</button>
                )}
                <button onClick={() => setDeleting(r)} className="pill bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`delete-${r.slug}`}><Trash2 size={12}/> حذف</button>
              </div>
            </div>
          );})}
        </div>
      </main>

      {showAdd && <AddRestaurantDialog onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {qrFor && <QRDialog data={qrFor} onClose={() => setQrFor(null)} />}
      {editing && <EditRestaurantDialog r={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resetPwFor && <ResetPasswordDialog r={resetPwFor} onClose={() => setResetPwFor(null)} onSaved={() => setResetPwFor(null)} />}
      {extendFor && <ExtendAccountDialog r={extendFor} onClose={() => setExtendFor(null)} onDone={() => { setExtendFor(null); load(); }} />}
      {deleting && <DeleteRestaurantDialog r={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function QRDialog({ data, onClose }) {
  const download = () => {
    const svg = document.getElementById("admin-qr-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 600; canvas.height = 600;
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 600, 600);
      ctx.drawImage(img, 0, 0, 600, 600);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png"); a.download = `qr-${data.slug}.png`; a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-center p-5" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-6 max-w-sm w-full text-center">
        <div className="font-extrabold text-lg mb-1">{data.name}</div>
        <div className="text-xs text-gray-500 mb-4 num-badge break-all">{data.url}</div>
        <div className="mx-auto inline-block p-3 rounded-2xl border border-orange-100 bg-white">
          <QRCodeSVG id="admin-qr-svg" value={data.url} size={220} level="M" bgColor="#ffffff" fgColor="#111827"/>
        </div>
        <div className="mt-4 flex gap-2 justify-center">
          <button onClick={download} className="pill brand-grad text-white px-4 py-2 font-bold text-sm">تحميل QR</button>
          <button onClick={onClose} className="pill bg-white border border-gray-200 px-4 py-2 font-bold text-sm">إغلاق</button>
        </div>
      </div>
    </div>
  );
}

function AddRestaurantDialog({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", owner_name: "", owner_email: "", owner_phone: "", owner_password: "",
    status: "TRIAL",
    free_until_launch_message: DEFAULT_FREE_UNTIL_LAUNCH_MESSAGE,
  });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name || !f.owner_name || !f.owner_email || !f.owner_password) return toast.error("أكمل الحقول المطلوبة");
    if (f.owner_password.length < 6) return toast.error("كلمة المرور 6 أحرف على الأقل");
    setBusy(true);
    try { await api.post("/admin/restaurants", f); toast.success("تم إنشاء المطعم"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">إضافة مطعم</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="space-y-2">
          <F label="اسم المحل" v={f.name} on={v => u("name", v)} tid="ar-name" />
          <F label="اسم صاحب المحل" v={f.owner_name} on={v => u("owner_name", v)} tid="ar-owner" />
          <F label="البريد الإلكتروني للمالك" type="email" v={f.owner_email} on={v => u("owner_email", v)} tid="ar-email" />
          <F label="رقم جوال المالك" v={f.owner_phone} on={v => u("owner_phone", v)} tid="ar-phone" />
          <F label="كلمة مرور مؤقتة" type="password" v={f.owner_password} on={v => u("owner_password", v)} tid="ar-password" />
          <div>
            <div className="text-xs font-bold text-gray-600 mb-1">حالة الحساب</div>
            <div className="grid grid-cols-2 gap-2">
              {["TRIAL","ACTIVE","SUSPENDED", FREE_UNTIL_LAUNCH].map(s => (
                <button key={s} onClick={() => u("status", s)} data-testid={`ar-status-${s}`}
                  className={`rounded-2xl p-2 border-2 text-sm font-bold ${f.status === s ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`}>
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
            {f.status === FREE_UNTIL_LAUNCH && (
              <label className="mt-2 block">
                <span className="text-xs font-bold text-gray-600">رسالة المطعم</span>
                <textarea
                  value={f.free_until_launch_message}
                  onChange={e => u("free_until_launch_message", e.target.value)}
                  data-testid="ar-free-message"
                  className={
                    "mt-1 min-h-24 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 "
                    + "py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 "
                    + "focus:ring-orange-100"
                  }
                />
              </label>
            )}
          </div>
        </div>
        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="ar-save">
          {busy ? "جاري…" : "إنشاء المطعم"}
        </button>
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text", tid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input type={type} value={v} onChange={e => on(e.target.value)} data-testid={tid}
        className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
    </label>
  );
}
function Row({ label, v, on, tid }) {
  return (
    <label className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm font-medium">{label}</span>
      <input type="checkbox" checked={!!v} onChange={e => on(e.target.checked)} data-testid={tid} className="w-5 h-5 accent-orange-600" />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Edit an existing restaurant + its owner contact.
// (Password is intentionally NOT included here — it lives behind its own dialog.)
// ---------------------------------------------------------------------------
function EditRestaurantDialog({ r, onClose, onSaved }) {
  const [f, setF] = useState({
    name: r.name || "",
    phone: r.phone || "",
    status: r.status || "TRIAL",
    free_until_launch_message: r.free_until_launch_message || DEFAULT_FREE_UNTIL_LAUNCH_MESSAGE,
    orders_accepting_now: r.orders_accepting_now !== false,
    owner_name: r.owner_name || "",
    owner_email: r.owner_email || "",
    owner_phone: r.owner_phone || "",
    additional_branch_limit: Number(r.branch_entitlement?.additional_branch_limit ?? 0),
  });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("اسم المطعم مطلوب");
    if (!f.owner_name.trim()) return toast.error("اسم المالك مطلوب");
    if (!f.owner_email.trim()) return toast.error("البريد الإلكتروني للمالك مطلوب");
    setBusy(true);
    try {
      await api.patch(`/admin/restaurants/${r.id}`, {
        name: f.name.trim(),
        phone: f.phone,
        status: f.status,
        free_until_launch_message: (
          f.status === FREE_UNTIL_LAUNCH
            ? f.free_until_launch_message
            : undefined
        ),
        orders_accepting_now: f.orders_accepting_now,
        owner_name: f.owner_name.trim(),
        owner_email: f.owner_email.trim().toLowerCase(),
        owner_phone: f.owner_phone,
        additional_branch_limit: Math.max(0, Number(f.additional_branch_limit) || 0),
      });
      toast.success("تم حفظ التعديلات");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">تعديل المطعم</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-bold text-gray-500 pt-1">معلومات المطعم</div>
          <F label="اسم المحل" v={f.name} on={v => u("name", v)} tid="edit-name" />
          <F label="رقم جوال المحل" v={f.phone} on={v => u("phone", v)} tid="edit-phone" />
          <div className="pt-1">
            <div className="text-xs font-bold text-gray-600 mb-1">حالة الاشتراك</div>
            <div className="grid grid-cols-2 gap-2">
              {["TRIAL","ACTIVE","SUSPENDED", FREE_UNTIL_LAUNCH].map(s => (
                <button key={s} onClick={() => u("status", s)} data-testid={`edit-status-${s}`}
                  className={`rounded-2xl p-2 border-2 text-sm font-bold ${f.status === s ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`}>
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
            {f.status === FREE_UNTIL_LAUNCH && (
              <label className="mt-2 block">
                <span className="text-xs font-bold text-gray-600">رسالة المطعم</span>
                <textarea
                  value={f.free_until_launch_message}
                  onChange={e => u("free_until_launch_message", e.target.value)}
                  data-testid="edit-free-message"
                  className={
                    "mt-1 min-h-24 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 "
                    + "py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 "
                    + "focus:ring-orange-100"
                  }
                />
              </label>
            )}
            <Row label="استقبال الطلبات الآن (مؤقت)" v={f.orders_accepting_now} on={v => u("orders_accepting_now", v)} tid="edit-accepting" />
            <div className="text-[11px] text-gray-500 -mt-1 mb-1">حالة الاشتراك مستقلة عن هذا المفتاح المؤقت للاستقبال.</div>
          </div>

          <div className="text-xs font-bold text-gray-500 pt-2">الفروع (باقة مدفوعة)</div>
          <F label="عدد الفروع الإضافية المسموح بها" type="number" v={f.additional_branch_limit} on={v => u("additional_branch_limit", v)} tid="edit-branch-limit" />
          <div className="text-[11px] text-gray-500 -mt-1">الفرع الرئيسي مشمول دائمًا. الحد الأقصى للفروع الفعّالة = 1 + هذا الرقم.</div>

          <div className="text-xs font-bold text-gray-500 pt-2">معلومات المالك</div>
          <F label="اسم صاحب المحل" v={f.owner_name} on={v => u("owner_name", v)} tid="edit-owner-name" />
          <F label="البريد الإلكتروني للمالك" type="email" v={f.owner_email} on={v => u("owner_email", v)} tid="edit-owner-email" />
          <F label="رقم جوال المالك" v={f.owner_phone} on={v => u("owner_phone", v)} tid="edit-owner-phone" />
          <div className="text-[11px] text-gray-500">لتغيير كلمة المرور استخدم زر «كلمة المرور» في بطاقة المطعم.</div>
        </div>
        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="edit-save">
          {busy ? "جاري…" : "حفظ التعديلات"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reset the owner's password (never displays or returns an existing password).
// ---------------------------------------------------------------------------
function ResetPasswordDialog({ r, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (pw.length < 6) return toast.error("كلمة المرور 6 أحرف على الأقل");
    if (pw !== pw2) return toast.error("تأكيد كلمة المرور غير مطابق");
    setBusy(true);
    try {
      await api.post(`/admin/restaurants/${r.id}/reset-owner-password`, { new_password: pw });
      toast.success("تم تعيين كلمة المرور الجديدة");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in" data-testid="reset-pw-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">تعيين كلمة مرور جديدة</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="text-sm text-gray-600 mb-3">
          لن يتم عرض كلمة المرور الحالية لأي طرف — يمكنك فقط تعيين كلمة مرور جديدة للمالك <span className="font-bold">{r.owner_name || r.owner_email}</span> في مطعم <span className="font-bold">{r.name}</span>.
        </div>
        <div className="space-y-2">
          <F label="كلمة المرور الجديدة" type="password" v={pw} on={setPw} tid="reset-pw-new" />
          <F label="تأكيد كلمة المرور" type="password" v={pw2} on={setPw2} tid="reset-pw-confirm" />
        </div>
        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="reset-pw-save">
          {busy ? "جاري…" : "حفظ كلمة المرور"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Archive (soft-delete) confirmation. We deliberately do NOT hard-delete because
// orders/products history is referenced elsewhere; archiving preserves data
// integrity while making the restaurant fully invisible to customers and staff.
// ---------------------------------------------------------------------------
function DeleteRestaurantDialog({ r, onClose, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const canConfirm = confirmText.trim() === r.name.trim();

  const del = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.delete(`/admin/restaurants/${r.id}`);
      toast.success("تم حذف المطعم");
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in" data-testid="delete-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg text-red-700">حذف مطعم</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="text-sm text-gray-700 space-y-2 mb-3">
          <div className="font-bold">هل أنت متأكد من حذف هذا المطعم؟</div>
          <div>
            سيتم إخفاء <span className="font-bold">{r.name}</span> عن العملاء نهائياً وإيقاف حسابات المالك والموظفين فوراً.
          </div>
          <div className="text-xs text-gray-600 rounded-xl bg-amber-50 border border-amber-100 p-2">
            لضمان سلامة السجلات، لا يتم حذف الطلبات السابقة أو المنتجات أو الفواتير من قاعدة البيانات — يتم أرشفتها فقط.
            هذا الإجراء يشمل: إخفاء صفحة الطلب العامة، وإيقاف تسجيل دخول المالك، وإيقاف كل الموظفين.
          </div>
        </div>
        <label className="block mb-2">
          <span className="text-xs font-bold text-gray-600">اكتب اسم المطعم للتأكيد:</span>
          <input value={confirmText} onChange={e => setConfirmText(e.target.value)} data-testid="delete-confirm-input"
            placeholder={r.name}
            className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
        </label>
        <button disabled={!canConfirm || busy} onClick={del}
          className={`w-full pill font-bold py-3 tap-target ${canConfirm ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500"}`}
          data-testid="delete-confirm-btn">
          {busy ? "جاري…" : "حذف نهائي"}
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Extend / reactivate the restaurant's account. Backend enforces days>=1 and
// the endpoint is idempotent — pressing again just adds more days.
// ---------------------------------------------------------------------------
function ExtendAccountDialog({ r, onClose, onDone }) {
  const [days, setDays] = useState(30);
  const [customDate, setCustomDate] = useState("");
  const [busy, setBusy] = useState(false);
  const isExpired = !!r.account?.is_expired;

  const extend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/restaurants/${r.id}/extend`, { days: Number(days) });
      toast.success(`تم تمديد الاشتراك ${days} يومًا`);
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const setExact = async () => {
    if (!customDate) return toast.error("اختر تاريخًا");
    setBusy(true);
    try {
      const iso = new Date(customDate + "T00:00:00Z").toISOString();
      await api.post(`/admin/restaurants/${r.id}/set-expiration`, { expires_at: iso });
      toast.success("تم تعيين تاريخ الانتهاء");
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/restaurants/${r.id}/reactivate`);
      toast.success("تم تفعيل الحساب من جديد");
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in" data-testid="extend-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">{isExpired ? "تفعيل الحساب" : "تمديد الاشتراك"}</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="text-sm text-gray-600 mb-3">
          {isExpired ? (
            <>هذا الحساب منتهي الصلاحية. اختر عدد الأيام لإعادة تفعيله، أو استخدم زر التفعيل السريع (14 يومًا).</>
          ) : (
            <>الأيام المتبقية حاليًا: <span className="num-badge font-bold text-gray-900">{r.account?.days_remaining ?? "-"}</span></>
          )}
        </div>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`ext-preset-${d}`}
              className={`rounded-2xl p-2 border-2 text-sm font-bold ${days === d ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`}>
              {d} يوم
            </button>
          ))}
        </div>
        <F label="أو أدخل عدد أيام مخصص" type="number" v={days} on={v => setDays(v)} tid="ext-days-custom" />
        <button disabled={busy} onClick={extend} className="mt-3 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="ext-add-days">
          {busy ? "جاري…" : `تمديد ${days} يومًا`}
        </button>

        <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
          <div className="text-xs font-bold text-gray-600">أو تعيين تاريخ نهاية مخصص</div>
          <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)} data-testid="ext-exact-date"
            className="w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
          <button disabled={busy || !customDate} onClick={setExact}
            className="w-full pill bg-white border border-orange-200 text-orange-700 font-bold py-2.5 tap-target disabled:opacity-60" data-testid="ext-set-exact">
            تعيين هذا التاريخ
          </button>
        </div>

        {isExpired && (
          <button disabled={busy} onClick={reactivate}
            className="mt-3 w-full pill bg-emerald-600 text-white font-bold py-3 tap-target disabled:opacity-60 inline-flex items-center justify-center gap-1" data-testid="ext-reactivate">
            <RotateCw size={16}/> تفعيل سريع (14 يومًا)
          </button>
        )}
      </div>
    </div>
  );
}

